import { useCallback, useMemo, useState } from "react";
import { Loader2, RefreshCw, Star } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingState } from "@/modules/collector_v2/components/common/LoadingState";
import { ErrorState } from "@/modules/collector_v2/components/common/ErrorState";
import { ProfileSummary } from "@/modules/collector_v2/components/profile/ProfileSummary";
import { HandHistoryTable } from "@/modules/collector_v2/components/profile/HandHistoryTable";
import { HandSummaryDrawer } from "@/modules/collector_v2/components/profile/HandSummaryDrawer";
import { useApi } from "@/modules/collector_v2/hooks/useApi";
import { apiDelete, apiPost, v2 } from "@/modules/collector_v2/lib/apiClient";
import { toast } from "sonner";

const PAGE_SIZE = 100;

export function PlayerProfileDrawer({ uid, open, onOpenChange }) {
  const numericUid = Number(uid);
  const enabled = open && Number.isFinite(numericUid) && numericUid > 0;

  const profilePath = useMemo(
    () => (enabled ? v2.profile(numericUid) : null),
    [enabled, numericUid],
  );
  const historyPath = useMemo(
    () => (enabled ? v2.history(numericUid, { limit: PAGE_SIZE, skip: 0 }) : null),
    [enabled, numericUid],
  );
  const collectionPath = useMemo(
    () => (enabled ? v2.collectionList(numericUid) : null),
    [enabled, numericUid],
  );

  const {
    data: profile,
    loading: profileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useApi(profilePath, [profilePath]);
  const {
    data: history,
    loading: historyLoading,
    error: historyError,
    refetch: refetchHistory,
  } = useApi(historyPath, [historyPath]);
  const {
    data: collection,
    loading: collectionLoading,
    error: collectionError,
    refetch: refetchCollection,
  } = useApi(collectionPath, [collectionPath]);

  const [drawerHandId, setDrawerHandId] = useState(null);
  const [handDrawerOpen, setHandDrawerOpen] = useState(false);
  const [busyHandId, setBusyHandId] = useState(null);

  const openHandDrawer = useCallback((hand) => {
    setDrawerHandId(hand.hand_id);
    setHandDrawerOpen(true);
  }, []);

  const refreshAll = useCallback(() => {
    refetchProfile?.();
    refetchHistory?.();
    refetchCollection?.();
  }, [refetchProfile, refetchHistory, refetchCollection]);

  const handleToggleCollect = useCallback(
    async (hand) => {
      if (!hand?.hand_id || !numericUid) return;
      const handId = hand.hand_id;
      setBusyHandId(handId);
      try {
        if (hand.is_collected) {
          await apiDelete(v2.collectionItem(numericUid, handId));
          toast.success?.("Mano removida de la colección");
        } else {
          await apiPost(v2.collectionItem(numericUid, handId));
          toast.success?.("Mano agregada a la colección");
        }
        await Promise.all([refetchHistory(), refetchCollection(), refetchProfile()]);
      } catch (e) {
        toast.error?.(e?.response?.data?.detail || e.message || "Error al actualizar colección");
      } finally {
        setBusyHandId(null);
      }
    },
    [numericUid, refetchHistory, refetchCollection, refetchProfile],
  );

  const handleToggleFromHandDrawer = useCallback(
    async (handId, isCollected) => {
      await handleToggleCollect({ hand_id: handId, is_collected: !!isCollected });
    },
    [handleToggleCollect],
  );

  const collectedHandIds = useMemo(() => {
    const set = new Set();
    (collection?.items || []).forEach((c) => set.add(c.hand_id));
    (history?.items || []).forEach((h) => {
      if (h.is_collected) set.add(h.hand_id);
    });
    return set;
  }, [collection, history]);

  const drawerIsCollected = drawerHandId ? collectedHandIds.has(drawerHandId) : false;
  const title = profile?.nick || (profileLoading ? "Cargando…" : `UID ${uid || "—"}`);
  const busy = profileLoading || historyLoading || collectionLoading;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-5xl"
          data-testid="player-profile-drawer"
        >
          <SheetHeader className="pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <SheetTitle className="text-xl font-semibold" data-testid="player-profile-drawer-title">
                  {title}
                </SheetTitle>
                <SheetDescription data-testid="player-profile-drawer-description">
                  {profile
                    ? `UID ${uid} · ${profile.hands_played ?? 0} manos · ${profile.tables_count ?? 0} mesas`
                    : `Perfil Collector V2 para UID ${uid || "—"}`}
                </SheetDescription>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <Button
                  data-testid="player-profile-drawer-refresh-button"
                  variant="ghost"
                  size="icon"
                  onClick={refreshAll}
                  disabled={busy}
                  title="Refrescar perfil"
                >
                  <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
                </Button>
                {/* La X de cerrar la provee SheetContent automáticamente.
                    No agregar un segundo botón aquí (causaba doble X). */}
              </div>
            </div>
          </SheetHeader>

          {profileLoading && !profile && <LoadingState />}
          {profileError && !profile && (
            <ErrorState message={profileError} onRetry={refetchProfile} />
          )}

          {profile && (
            <Tabs defaultValue="resumen" className="w-full" data-testid="player-profile-drawer-tabs">
              <TabsList className="mb-4 grid w-full grid-cols-3">
                <TabsTrigger value="resumen" data-testid="player-drawer-tab-resumen">
                  Resumen
                </TabsTrigger>
                <TabsTrigger value="historial" data-testid="player-drawer-tab-historial">
                  Historial ({profile.hands_played ?? 0})
                </TabsTrigger>
                <TabsTrigger value="coleccion" data-testid="player-drawer-tab-coleccion">
                  <Star className="mr-1.5 h-3.5 w-3.5" />
                  Colección ({profile.collected_hands_count ?? 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="resumen" className="space-y-4">
                <ProfileSummary profile={profile} />
              </TabsContent>

              <TabsContent value="historial" className="space-y-4">
                {historyLoading && !history && (
                  <Card>
                    <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Cargando últimas {PAGE_SIZE} manos…
                    </CardContent>
                  </Card>
                )}
                {historyError && <ErrorState message={historyError} onRetry={refetchHistory} />}
                {history && (
                  <Card>
                    <CardContent className="pt-6">
                      <HandHistoryTable
                        items={history.items || []}
                        onToggleCollect={handleToggleCollect}
                        onOpenReplay={openHandDrawer}
                        busyHandId={busyHandId}
                        emptyLabel="Este jugador no tiene manos registradas todavía."
                      />
                      {history.count > history.items?.length && (
                        <p
                          className="mt-3 text-center text-xs text-muted-foreground"
                          data-testid="player-drawer-history-pagination-hint"
                        >
                          Mostrando {history.items?.length || 0} de {history.count} manos.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="coleccion" className="space-y-4">
                {collectionLoading && !collection && (
                  <Card>
                    <CardContent className="flex items-center justify-center py-12 text-muted-foreground">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Cargando colección…
                    </CardContent>
                  </Card>
                )}
                {collectionError && <ErrorState message={collectionError} onRetry={refetchCollection} />}
                {collection && (
                  <Card>
                    <CardContent className="pt-6">
                      <HandHistoryTable
                        items={(collection.items || []).map((it) => ({ ...it, is_collected: true }))}
                        onToggleCollect={handleToggleCollect}
                        onOpenReplay={openHandDrawer}
                        busyHandId={busyHandId}
                        emptyLabel="Aún no hay manos coleccionadas. Marcá una con la ⭐ desde Historial."
                      />
                      {collection.count > 0 && (
                        <p
                          className="mt-3 text-center text-xs text-muted-foreground"
                          data-testid="player-drawer-collection-count-hint"
                        >
                          {collection.count} mano{collection.count === 1 ? "" : "s"} en colección.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      <HandSummaryDrawer
        handId={drawerHandId}
        isCollected={drawerIsCollected}
        onToggleCollect={handleToggleFromHandDrawer}
        open={handDrawerOpen}
        onOpenChange={setHandDrawerOpen}
      />
    </>
  );
}
